import React, {useMemo, useRef} from 'react';
import {useParams, useSearchParams} from 'react-router';
import {Box, Divider, Stack, Typography} from '@mui/material';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import previewTheme from '../../../products/previewPage/previewTheme';
import EvidenceModal from '../../../../components/EvidenceModal';
import ReferencesList from '../../../products/previewPage/ReferencesList';
import useApprovedReportData from '../../../products/reportPage/useApprovedReportData';
import useReferenceProcessing from '../../../products/previewPage/useReferenceProcessing';
import ReportContent from '../../../products/previewPage/ReportContent';
import {ErrorState, LoadingState, NoContentState} from '../../../products/previewPage/PreviewStates';
import UserMentionPopover from '../../../../components/prosemirror-editor/components/UserMentionPopover';
import HashtagPopover from '../../../../components/prosemirror-editor/components/HashtagPopover';
import {useUserMentionPopover} from '../../../../components/prosemirror-editor/hooks/useUserMentionPopover';
import {useHashtagPopover} from '../../../../components/prosemirror-editor/hooks/useHashtagPopover';
import {buildExcerptSearchTerms, useReportExcerptScroll} from '../../../products/previewPage/useReportExcerptScroll';

/**
 * Full-fidelity report preview used in the feed's right panel.
 *
 * The :sourceSegmentId URL param is set to item.reportId by FeedItemStack when
 * a FINTEL card is clicked. For FINTEL items the whole sourceSegmentId IS the
 * reportId (e.g. "fintel-uav-10427-003"), so we use it directly without splitting.
 */
const FeedReportPreview = () => {
    const {sourceSegmentId} = useParams();

    // For FINTEL feed items the entire sourceSegmentId is the report ID.
    const reportId = sourceSegmentId || null;

    const [selectedAuthorId, setSelectedAuthorId] = React.useState(null);
    const contentRef = useRef(null);
    const scrollContainerRef = useRef(null);
    const [searchParams] = useSearchParams();

    // matchedSegmentText passed as ?q= — scroll to the matching excerpt after render.
    const searchTerms = useMemo(
        () => buildExcerptSearchTerms(searchParams.get('q')),
        [searchParams],
    );

    const {
        anchorEl: mentionAnchorEl,
        anchorPosition: mentionAnchorPosition,
        userData: mentionUserData,
        open: mentionPopoverOpen,
        handleUserMentionClick,
        handleClose: handleMentionPopoverClose,
    } = useUserMentionPopover();

    const {
        anchorEl: hashtagAnchorEl,
        anchorPosition: hashtagAnchorPosition,
        tagData: hashtagData,
        open: hashtagPopoverOpen,
        handleHashtagClick,
        handleClose: handleHashtagPopoverClose,
    } = useHashtagPopover();

    const {
        version,
        latestVersion,
        reportDoc,
        headerDoc,
        content,
        references,
        meta,
        documentHeader,
        previewAuthors,
        isLoading,
        error,
    } = useApprovedReportData(reportId);

    const {
        localReferences,
        referenceNumbers,
        referencesByMarkId,
        getReferenceNumber,
    } = useReferenceProcessing(content, references);

    const handleAuthorClick = (authorId) => {
        setSelectedAuthorId((prev) => (prev === authorId ? null : authorId));
    };

    useReportExcerptScroll(contentRef, scrollContainerRef, searchTerms, isLoading, Boolean(content));

    if (!sourceSegmentId) {
        return (
            <Stack flex={1} alignItems="center" justifyContent="center" height="100%">
                <Typography color="text.secondary">Select a report to preview</Typography>
            </Stack>
        );
    }

    return (
        <>
            <Box
                ref={scrollContainerRef}
                data-report-scroll-root
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    overflowY: 'auto',
                    bgcolor: 'white',
                    height: '100%',
                }}
            >
                <ThemeProvider theme={previewTheme}>
                    <CssBaseline/>
                    <Stack
                        className="preview-root"
                        spacing={2}
                        sx={{padding: 2}}
                        maxWidth="800px"
                        margin="0 auto"
                        width="100%"
                    >
                        {isLoading && <LoadingState/>}
                        {error && !isLoading && <ErrorState error={error}/>}
                        {version && !isLoading && !error && !content && <NoContentState version={version}/>}

                        {!isLoading && !error && content && (
                            <div ref={contentRef}>
                                <ReportContent
                                    content={content}
                                    meta={meta}
                                    documentHeader={documentHeader}
                                    previewAuthors={previewAuthors}
                                    reportDoc={headerDoc || reportDoc}
                                    version={version}
                                    latestVersion={latestVersion}
                                    referencesByMarkId={referencesByMarkId}
                                    referenceNumbers={referenceNumbers}
                                    getReferenceNumber={getReferenceNumber}
                                    selectedAuthorId={selectedAuthorId}
                                    onAuthorClick={handleAuthorClick}
                                    onUserMentionClick={handleUserMentionClick}
                                    onHashtagClick={handleHashtagClick}
                                />
                            </div>
                        )}

                        {!isLoading && !error && content && (
                            <>
                                <Divider/>
                                <ReferencesList
                                    references={localReferences}
                                    getReferenceNumber={getReferenceNumber}
                                />
                            </>
                        )}
                    </Stack>
                </ThemeProvider>
            </Box>

            <EvidenceModal/>

            <UserMentionPopover
                open={mentionPopoverOpen}
                anchorEl={mentionAnchorEl}
                anchorPosition={mentionAnchorPosition}
                onClose={handleMentionPopoverClose}
                userData={mentionUserData}
            />

            <HashtagPopover
                open={hashtagPopoverOpen}
                anchorEl={hashtagAnchorEl}
                anchorPosition={hashtagAnchorPosition}
                onClose={handleHashtagPopoverClose}
                tagData={hashtagData}
            />
        </>
    );
};

export default FeedReportPreview;
